import { request } from './client.js';

/**
 * Загрузка фотографий (E3): presigned PUT прямо в S3, затем подтверждение.
 * Сервер срезает EXIF и строит производные — здесь только выбор файла
 * и клиентская проверка, чтобы человек узнал о проблеме до загрузки (§5.5).
 */

export const UPLOAD_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'] as const;
export const AUDIO_UPLOAD_MIME_TYPES = [
  'audio/webm',
  'audio/ogg',
  'audio/mpeg',
  'audio/wav',
  'audio/mp4',
] as const;
export type UploadMime = (typeof UPLOAD_MIME_TYPES)[number];
export type AudioUploadMime = (typeof AUDIO_UPLOAD_MIME_TYPES)[number];
const AUDIO_MIME_BY_EXTENSION: Readonly<Record<string, AudioUploadMime>> = {
  webm: 'audio/webm',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  mp4: 'audio/mp4',
};
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
/** Ниже 512 px по короткой стороне лицо не читается (§5.5). */
export const MIN_PHOTO_EDGE = 512;

export type PhotoIssue = 'too_large' | 'unsupported_type' | 'too_small';

export interface ConfirmedAsset {
  readonly id: string;
  readonly kind: string;
  readonly mime: UploadMime;
  readonly bytes: number;
  readonly width: number;
  readonly height: number;
  readonly url: string;
  readonly urlExpiresAt: string;
}

export interface ConfirmedAudioAsset {
  readonly id: string;
  readonly kind: 'voice_sample' | 'narration_audio';
  readonly mime: AudioUploadMime;
  readonly bytes: number;
  readonly durationMs: number;
  readonly url: string;
  readonly urlExpiresAt: string;
}

interface PresignResponse {
  readonly storageKey: string;
  readonly uploadUrl: string;
  readonly expiresAt: string;
  readonly headers: {
    readonly 'content-type': UploadMime;
    readonly 'cache-control'?: string;
  };
}

/**
 * Чистая проверка выбранного файла. Маленькое разрешение — предупреждение,
 * а не запрет: правило §5.5 прямо требует не блокировать (`too_small`
 * возвращается вместе с остальными, решение принимает вызывающий).
 */
export function inspectPhoto(input: {
  readonly mime: string;
  readonly bytes: number;
  readonly width?: number | null;
  readonly height?: number | null;
}): { readonly blocking: PhotoIssue[]; readonly warnings: PhotoIssue[] } {
  const blocking: PhotoIssue[] = [];
  const warnings: PhotoIssue[] = [];
  if (!UPLOAD_MIME_TYPES.some((mime) => mime === input.mime)) blocking.push('unsupported_type');
  if (input.bytes > MAX_UPLOAD_BYTES) blocking.push('too_large');
  const edge = Math.min(input.width ?? 0, input.height ?? 0);
  if (edge > 0 && edge < MIN_PHOTO_EDGE) warnings.push('too_small');
  return { blocking, warnings };
}

/**
 * What a photo input offers, and deliberately without HEIC.
 *
 * iOS converts a photo picked from the library to JPEG on the phone, but only while the
 * input does not ask for HEIC: list `image/heic` and Safari hands over the HEIC as is, and
 * by one report even turns picked JPEGs into HEIC. The api converts any HEIC that does
 * arrive (a file chosen in Files, one AirDropped to a laptop); this keeps that the rare case
 * instead of every iPhone upload. The cost: a desktop whose OS has no HEIF type may hide a
 * `.heic` behind "All files" in the dialog.
 */
export const PHOTO_ACCEPT = 'image/*';

/**
 * The type of a photograph, by its name when the browser gives none. Chrome and Firefox on
 * a desktop report `.heic` as an empty string — they cannot read it themselves — and the
 * check before upload would refuse an iPhone photo the server accepts. The server checks
 * the signature either way.
 */
export function photoUploadMime(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return extension === 'heic' ? 'image/heic' : file.type;
}

/**
 * Тип записи по имени файла: для `.opus` и `.oga` браузер часто отдаёт пустую
 * строку или свой вариант mime, и presign отклонил бы файл, который сервер
 * принимает. Расширение здесь — подсказка, сигнатуру всё равно проверяет сервер.
 */
export function audioUploadMime(file: File): string {
  if (AUDIO_UPLOAD_MIME_TYPES.some((mime) => mime === file.type)) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return AUDIO_MIME_BY_EXTENSION[extension] ?? file.type;
}

export type UploadKind = 'photo' | 'moodboard' | 'thing_ref' | 'location_ref';

export async function uploadPhoto(file: File, kind: UploadKind = 'photo'): Promise<ConfirmedAsset> {
  const presigned = await request<PresignResponse>('/uploads/presign', {
    method: 'POST',
    body: { filename: file.name, mime: photoUploadMime(file), bytes: file.size, kind },
  });

  const uploaded = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: presigned.headers,
    body: file,
  });
  if (!uploaded.ok) throw new Error(`upload failed: ${uploaded.status}`);

  return request<ConfirmedAsset>('/uploads/confirm', {
    method: 'POST',
    body: { storageKey: presigned.storageKey, kind },
  });
}

export async function uploadAudio(
  file: File,
  kind: 'voice_sample' | 'narration_audio',
  voiceId?: string,
): Promise<ConfirmedAudioAsset> {
  const common = { kind, ...(voiceId ? { voiceId } : {}) };
  const presigned = await request<{
    storageKey: string;
    uploadUrl: string;
    headers: { 'content-type': AudioUploadMime; 'cache-control'?: string };
  }>('/uploads/presign', {
    method: 'POST',
    body: { filename: file.name, mime: audioUploadMime(file), bytes: file.size, ...common },
  });
  const uploaded = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: presigned.headers,
    body: file,
  });
  if (!uploaded.ok) throw new Error(`upload failed: ${uploaded.status}`);
  return request<ConfirmedAudioAsset>('/uploads/confirm', {
    method: 'POST',
    body: { storageKey: presigned.storageKey, ...common },
  });
}
